import { Language } from "@mui/icons-material";
import { Box, Card, CardContent, IconButton, Menu, MenuItem, Tooltip, Typography } from "@mui/material";
import { AxiosError } from "axios";
import { GB, IT, SI } from 'country-flag-icons/react/3x2';
import I18n, { changeLanguage } from 'i18next';
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import Background from "../../assets/background.png";
import LoginLogo from "../../assets/beetogether-logo-520x520.png";
import CustomForm from "../../common/components/CustomForm";
import CustomSnackbar from "../../common/components/CustomSnackbar";
import { getForm } from "../../common/config/forms";
import { IChangePasswordBody, ICompleteChangePasswordBody } from "../../common/interfaces/auth";
import authService from "../../common/services/authService";

interface IChangePasswordProps { }

const ChangePassword: FC<IChangePasswordProps> = () => {
    // hooks
    const navigate = useNavigate();
    const locale = I18n.language;
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();

    const [anchorElLang, setAnchorElLang] = useState<null | HTMLElement>(null);
    const [language, setLanguage] = useState(locale);
    const [emailAddress, setEmailAddress] = useState("");
    const [restoreKey, setRestoreKey] = useState("");

    const lang = [
        { flag: <GB title="English" />, label: I18n.t("EN", { locale: language }), value: "en-EN" },
        { flag: <IT title="Italy" />, label: I18n.t("IT", { locale: language }), value: "it-IT" },
        { flag: <SI title="Slovenian" />, label: I18n.t("SL", { locale: language }), value: "sl-SI" },
    ]

    // form
    const changePasswordForm = getForm<IChangePasswordBody>("changePassword");

    // snackbar
    const [openSnackbar, setOpenSnackbar] = useState<boolean>(false);
    const [message, setMessage] = useState<string>("");
    const [responseType, setResponseType] = useState<"success" | "error">(
        "success"
    );

    const initialValuesCompleteChangePassword: IChangePasswordBody = {
        newPassword: "",
        confirmNewPassword: ""
    };

    useEffect(() => {
        const email = searchParams.get("emailAddress")
        const restoreKey = searchParams.get("restoreKey")
        if (email)
            setEmailAddress(email)
        if (restoreKey)
            setRestoreKey(restoreKey)
    }, [searchParams])

    const handleCompleteChangePassword = async (data: IChangePasswordBody) => {
        if (emailAddress && restoreKey && data.newPassword) {
            const completeBody: ICompleteChangePasswordBody = {
                emailAddress,
                restoreKey,
                newPassword: data.newPassword
            }
            try {
                await authService.completeChangePassword(completeBody)
                setMessage(t("changePassword.successMessage"));
                setResponseType("success");
                setOpenSnackbar(true);
                navigate("/password-set");
            } catch (error) {
                if (error instanceof AxiosError) {
                    setMessage(error.response?.data.content.message);
                } else {
                    setMessage(t("genericError"));
                }
                setResponseType("error");
                setOpenSnackbar(true);
            }
        } else {
            setMessage(t("genericError"));
            setResponseType("error");
            setOpenSnackbar(true);
        }
    };

    const handleOpenLangMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorElLang(event.currentTarget);
    };

    const handleCloseLangMenu = (option: { label: string, value: string }) => {
        if (locale !== option.value) {
            setLanguage(option.value)
            changeLanguage(option.value);
        }
        setAnchorElLang(null);
    };

    return <Box sx={
        {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100vh",
            backgroundRepeat: "none",
            backgroundSize: "cover",
            backgroundImage: `url(${Background})`,
            backgroundPosition: "center"
        }
    }>
        <CustomSnackbar type={responseType} message={message} open={openSnackbar} onClose={() => setOpenSnackbar(false)} />
        <Card sx={{ width: 380, p: 1, m: 2, position: "relative" }}>
            <Tooltip title={t("header.selectLanguage")} sx={{ position: "absolute", top: 8, right: 12 }}>
                <IconButton onClick={handleOpenLangMenu} color="primary">
                    <Language />
                </IconButton>
            </Tooltip>
            <Menu
                sx={{ mt: '45px' }}
                id="menu-appbar"
                anchorEl={anchorElLang}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                open={Boolean(anchorElLang)}
                onClose={handleCloseLangMenu}
            >
                {lang.map((option) => (
                    <MenuItem key={option.label} onClick={() => handleCloseLangMenu(option)}>
                        <Box sx={{
                            width: 28,
                            height: 19,
                            mr: 1
                        }}>{option.flag}</Box>
                        <Typography textAlign="center">{option.label}</Typography>
                    </MenuItem>
                ))}
            </Menu>
            <Box sx={{ display: "flex", justifyContent: "center", m: 2, mb: 0 }}>
                <Box
                    component="img"
                    sx={{ height: "160px" }}
                    src={LoginLogo}
                    title="bee2gether-logo"
                ></Box>
            </Box>
            <CardContent sx={{ display: "flex", flexDirection: "column" }}>
                <Typography gutterBottom variant="h6" textAlign="center" component="div">
                    {t("changePassword.title")}
                </Typography>
                <CustomForm
                    {...changePasswordForm}
                    initialValues={initialValuesCompleteChangePassword}
                    submit={handleCompleteChangePassword}
                    labelButtonSubmit={t("changePassword.resetPassword")}
                    customStyles={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                    }}
                    isDialog={false}
                />
            </CardContent>
        </Card>
    </Box>
}

export default ChangePassword;